import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { SummaryComponent } from "../../summary/summary";
import { config } from "@config";
import { BpModalHasSave } from "@models/interfaces/bp-modal-has-save";
import { Fiat, PaymentOption } from "@models";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { ErrorClass, ForbiddenError, ValidationError } from "bp-common-models-lib";
import { FiatTransaction } from "@models/transaction";
import { SegmentService } from "@main-services/segment.service";
import { FiatService } from "@main-services/currency";
import { ApicallOption } from "@core-services/apicall.service";
import { ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { TranslationService } from "@core-services/translation.service";
import { InfoService } from "@core-services/info.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { filter, map } from "rxjs/operators";
import { FiatDepositRemindModalComponent } from "@modules/main/components/fiat-deposit-remind-modal/fiat-deposit-remind-modal.component";
import { BestFacade } from "@modules/new_account/features/my-best/domain/+state/best.facade";
import { BestModel } from "@modules/new_account/features/my-best/domain/entities/best.model";
import { RudderstackService } from "@main-services/rudderstack.service";

@UntilDestroy()
@Component({
	selector: "fiat-deposit-summary",
	templateUrl: "./fiat-deposit-summary-modal.component.html",
	styleUrls: ["./fiat-deposit-summary-modal.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class FiatDepositSummaryComponent extends SummaryComponent implements OnInit, BpModalHasSave {
	@Output()
	public errorCloseEmitter: EventEmitter<void> = new EventEmitter();
	public tac: boolean = false;
	public twoFAFiller: boolean = false;
	public currentBestRewardLevel: number;
	public provider: PaymentOption = null;
	public fiat: Fiat = null;
	protected label: string = "deposit";
	protected category: string = "fiat_transaction";
	private readonly flowName: string = "Deposit";
	private readonly pathPrepend: string = "/deposit/fiat";

	public constructor(
		private segmentService: SegmentService,
		private rudderstackService: RudderstackService,
		private masterDataService: MasterdataService,
		private fiatService: FiatService,
		private fiatWalletService: FiatWalletService,
		private modalService: ModalService,
		private translationService: TranslationService,
		private infoService: InfoService,
		private bestFacade: BestFacade
	) {
		super();
	}

	private _offer: FiatTransaction = null;

	public get offer(): FiatTransaction {
		return this._offer;
	}

	@Input()
	public set offer(value: FiatTransaction) {
		this._offer = value;
		this.provider = this.masterDataService.getPaymentOptionById(value.getPaymentOptionId());
		this.fiat = this.fiatService.getItemById(value.getFiatId());
	}

	public providerTermsChecked: boolean = false;

	public get providerTerms(): boolean {
		return this.providerTermsChecked || this.getProviderTerms() === "";
	}

	public set providerTerms(value: boolean) {
		this.providerTermsChecked = value;
		this.tac = value;
	}

	public get accepted(): boolean {
		return this.tac && this.providerTerms;
	}

	public set accepted(b: boolean) {
		this.tac = b;
		this.providerTerms = b;
	}

	public get createdFwt(): boolean {
		return !this.offer.isNew();
	}

	public get type(): string {
		return this.offer.getUserWantsType();
	}

	public get title(): string {
		if (this.twoFAFiller) {
			return this.translationService.translate("confirm_transaction");
		}
		let header = this.translationService.translate("deposit");

		if (this.fiat) {
			header += " " + this.fiat.getName();
		}
		return header;
	}

	public ngOnInit(): void {
		this.subscribeBestReward();
	}

	public hasVipDiscount(): boolean {
		return +this.offer.getBfcDifferenceAmount() !== 0;
	}

	public save(): void {
		if (!this.createdFwt) {
			this.createDeposit();
		}
	}

	public getDepositToolTipLink(): string {
		return this.translationService.getHdLink() + config.externalPaths["deposit"]["tooltip"];
	}

	public createDeposit(): void {
		if (this.offer.requested) {
			return;
		}
		if (!this.tac) {
			this.wiggle("tac");
		}
		if (!this.providerTerms) {
			this.wiggle("providerTerms");
		}
		if (!(this.tac && this.providerTerms)) {
			this.infoService.setError(this.translationService.translate("info_text_offer_checkboxes_not_checked"));
			return;
		}

		this.offer.requested = this.tac && this.providerTerms;

		// QR deposit modal flow for SEPA / Bank transfer
		if (this.provider.isManual) {
			this.segmentService.page({
				name: `${this.flowName} Bank Details`,
				overridePath: `${this.pathPrepend}/bankdetails`,
				properties: {
					fiat_currency: this.fiat.getSymbol(),
					deposit_method: this.provider.getShortName(),
					deposit_amount: "" + this.getOfferAmount()
				}
			});
			this.rudderstackService.page({
				name: `${this.flowName} Bank Details`,
				overridePath: `${this.pathPrepend}/bankdetails`,
				properties: {
					fiat_currency: this.fiat.getSymbol(),
					deposit_method: this.provider.getShortName(),
					deposit_amount: "" + this.getOfferAmount()
				}
			});
			return this._showDepositDetails();
		}

		this.segmentService.page({
			name: `${this.flowName} Redirect`,
			overridePath: `${this.pathPrepend}/${this.provider.getShortName()}`,
			properties: {
				fiat_currency: this.fiat.getSymbol(),
				deposit_method: this.provider.getShortName(),
				deposit_amount: "" + this.getOfferAmount()
			}
		});
		this.rudderstackService.page({
			name: `${this.flowName} Redirect`,
			overridePath: `${this.pathPrepend}/${this.provider.getShortName()}`,
			properties: {
				fiat_currency: this.fiat.getSymbol(),
				deposit_method: this.provider.getShortName(),
				deposit_amount: "" + this.getOfferAmount()
			}
		});

		// Other providers flow (eg: Sofort, Visa, Mastercard)
		this._makeDeposit();
	}

	public getProviderTerms(): string {
		if (this.provider == null) {
			return "";
		}
		return this.provider.getTerms();
	}

	public setEdit(e: number): void {
		if (this.createdFwt) {
			return;
		}
		this.edit.emit(e);
	}

	public close(): void {
		this.onClose.emit();
	}

	public getOfferAmount(): number {
		return this.offer.getAmount();
	}

	public getSummaryNotification(): string {
		if (this.provider == null) {
			return "";
		}
		return this.provider.getSummaryNotification();
	}

	private subscribeBestReward(): void {
		this.bestFacade
			.getBestState$()
			.pipe(
				filter((state: BestModel) => !!state),
				untilDestroyed(this)
			)
			.subscribe((result: BestModel) => {
				this.currentBestRewardLevel = result?.current_vip_levels_threshold?.level;
				this.masterDataService.markPaymentOptionsAsOutdated();
			});
	}

	private _showDepositDetails(): void {
		this.modalService
			.create(FiatDepositRemindModalComponent, {
				transaction: this.offer,
				provider: this.provider
			})
			.present();
		this.finish.emit(null);
	}

	private _makeDeposit(): void {
		const option = new ApicallOption(FiatTransaction, {
			amount: this.getOfferAmount(),
			payment_option_id: this.offer.getPaymentOptionId(),
			fiat_id: this.fiat.getPid(),
			expected_fee: "" + this.offer.getFee()
		});
		option.apiVersion = "v2";
		this.fiatWalletService
			.createDeposit(this.offer.getFiatWalletId(), option)
			.pipe(map((result: FiatTransaction[]) => result[0]))
			.subscribe(
				(ret: FiatTransaction) => {
					this.twoFAFiller = true;
					this.offer = ret;
					this.finish.emit(this.offer);
				},
				(error) => {
					this.offer.requested = false;
					if (error instanceof ErrorClass) {
						this.infoService.setError(error);
						if (error instanceof ValidationError) {
							this.offer.setValidationErrors(error);
						} else if (error instanceof ForbiddenError) {
							this.errorCloseEmitter.emit(); // Closes everything when forbidden error is received
						} else {
							this.infoService.setError(error);
						}
					} else {
						console.error(error);
					}
				}
			);
	}
}
